import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, cn, inr, fmtDate, whatsappShareUrl } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Chip, Empty, Input, Select, Sheet, Skeleton } from "../components/ui";
import {
  Wallet, ArrowUpRight, ArrowDownRight, PlusCircle, Check, ChevronDown, Share2, Receipt, Users,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const CATS = ["", "ground", "ball", "water", "snacks", "jersey", "transport", "trophy", "topup", "other"];
const TABS = [
  { key: "matches", label: "By match" },
  { key: "members", label: "By member" },
  { key: "all", label: "All entries" },
];

// How a player's share of a charge stands
const STATUS = {
  paid: { label: "Paid", tone: "success" },
  wallet: { label: "Paid from wallet", tone: "success" },
  partial: { label: "Part paid", tone: "warning" },
  pending: { label: "Pending", tone: "danger" },
};

/**
 * Club money for owners / admins / managers. After a match the admin posts the charges and
 * they're split among the players; this page answers: how much is due, how much came in,
 * how much is still pending — and who has / hasn't paid.
 */
export default function LedgerPage() {
  const { club } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [tab, setTab] = useState("matches");
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [topupOpen, setTopupOpen] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);

  const load = async () => {
    try {
      const [{ data: s }, { data: mem }] = await Promise.all([api.get("/ledger/summary"), api.get("/members")]);
      setSummary(s);
      setMembers(mem);
    } catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (new URLSearchParams(loc.search).get("topup") === "1") setTopupOpen(true);
  }, [loc.search]);

  /** Mark one or more charges as paid (admin collected the money). */
  const markPaid = async (items, who) => {
    const total = items.reduce((a, i) => a + i.outstanding, 0);
    const msg = items.length === 1
      ? `Mark ${inr(total)} from ${who} as paid?`
      : `Mark all ${items.length} pending charges (${inr(total)}) from ${who} as paid?`;
    if (!window.confirm(msg)) return;
    setMarkingPaid(items.length === 1 ? items[0].txn_id : `all-${who}`);
    try {
      for (const i of items) await api.post(`/transactions/${i.txn_id}/mark-paid`);
      toast.success(items.length === 1 ? "Marked as paid ✓" : `${items.length} charges marked as paid ✓`);
      await load();
    } catch (err) {
      toast.error(apiError(err));
      await load();
    } finally { setMarkingPaid(null); }
  };

  const t = summary?.totals;
  const pct = t && t.charged > 0 ? Math.round((t.collected / t.charged) * 100) : 0;

  return (
    <div className="pb-28">
      <TopBar title="Ledger" subtitle="Match charges & payments" />

      <div className="px-5 space-y-4">
        {/* What's due, what came in, what's left */}
        {!summary ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="rounded-3xl bg-ink text-white p-5 shadow-card" data-testid="ledger-summary">
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">Still to collect</div>
            <div className={cn("text-3xl font-extrabold tabular-nums mt-0.5", t.pending > 0 ? "text-red-400" : "text-white")}>
              {inr(t.pending)}
            </div>
            <div className="text-xs text-white/60 mt-0.5">
              {t.pending > 0
                ? `from ${t.members_pending} member${t.members_pending === 1 ? "" : "s"}`
                : t.charged > 0 ? "Everyone has paid 🎉" : "No charges posted yet"}
            </div>

            <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-4">
              <div className="h-full rounded-full bg-[#7CC242] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <Figure label="Total charged" value={inr(t.charged)} />
              <Figure label="Collected" value={inr(t.collected)} className="text-[#9BD86A]" />
              <Figure label="Collected %" value={`${pct}%`} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" className="h-11" onClick={() => nav("/expenses/new")} data-testid="ledger-add-expense">
            <PlusCircle size={16} /> Add charge
          </Button>
          <Button variant="outline" size="sm" className="h-11" onClick={() => setTopupOpen(true)} data-testid="record-topup-btn">
            <Wallet size={16} /> Record payment
          </Button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-1 p-1 bg-black/[0.04] rounded-2xl" role="tablist">
          {TABS.map((x) => (
            <button
              key={x.key}
              role="tab"
              aria-selected={tab === x.key}
              onClick={() => setTab(x.key)}
              className={cn("h-10 rounded-xl text-sm font-semibold", tab === x.key ? "bg-white shadow-sm text-ink" : "text-ink-muted")}
              data-testid={`ledger-tab-${x.key}`}
            >
              {x.label}
            </button>
          ))}
        </div>

        {tab === "matches" && (
          !summary ? <Skeleton className="h-64" /> :
          summary.charges.length === 0 ? (
            <Card className="p-0"><Empty icon={Receipt} title="No charges yet" subtitle="Post the match fee from a poll after the game, or add a charge." /></Card>
          ) : (
            <div className="space-y-3">
              {summary.charges.map((c) => (
                <ChargeCard key={c.id} c={c} club={club} markingPaid={markingPaid}
                  onMark={(p) => markPaid([p], p.name)} />
              ))}
            </div>
          )
        )}

        {tab === "members" && (
          !summary ? <Skeleton className="h-64" /> :
          summary.members.length === 0 ? (
            <Card className="p-0"><Empty icon={Users} title="No charges yet" subtitle="Members show up here once a charge is posted." /></Card>
          ) : (
            <MemberList rows={summary.members} club={club} markingPaid={markingPaid} onMark={markPaid} />
          )
        )}

        {tab === "all" && <AllEntries members={members} />}
      </div>

      <TopupSheet open={topupOpen} onClose={() => setTopupOpen(false)} members={members} onSaved={load} />
    </div>
  );
}

function Figure({ label, value, className }) {
  return (
    <div className="min-w-0">
      <div className={cn("text-base font-bold tabular-nums truncate", className)}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold leading-tight">{label}</div>
    </div>
  );
}

function Progress({ value, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
      <div className={cn("h-full rounded-full", pct >= 100 ? "bg-brand" : "bg-amber-500")} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** One posted charge (usually a match): totals, then every player Paid / Pending. */
function ChargeCard({ c, club, markingPaid, onMark }) {
  const unpaid = c.players.filter((p) => p.outstanding > 0);
  const [open, setOpen] = useState(unpaid.length > 0 && c.pending > 0);
  const perHead = c.players.length ? c.total / c.players.length : 0;

  const reminder = () => {
    const lines = [
      `💸 *${c.title}* — ${fmtDate(c.date)}`,
      `${inr(Math.round(perHead))}/head • ${unpaid.length} payment${unpaid.length === 1 ? "" : "s"} pending:`,
      "",
      ...unpaid.map((p, i) => `${i + 1}. ${p.name}${p.guest ? " (Guest)" : ""} — ${inr(p.outstanding)}`),
    ];
    if (club?.upi_id) lines.push("", `Pay by UPI: ${club.upi_id}`);
    lines.push("", "Please pay and mark it as paid in Cricso 🙏", "", "Thanks,", "Cricso", "Powered by HMT Technologies");
    window.open(whatsappShareUrl(lines.join("\n")), "_blank");
  };

  return (
    <Card className="p-0 overflow-hidden" data-testid={`charge-${c.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-bold truncate">{c.title}</div>
            <div className="text-xs text-ink-muted">
              {fmtDate(c.date)} • {c.players.length} player{c.players.length === 1 ? "" : "s"} • {inr(Math.round(perHead))}/head
            </div>
          </div>
          <div className="text-right shrink-0">
            {c.pending > 0
              ? <div className="text-sm font-bold text-red-500 tabular-nums">{inr(c.pending)} due</div>
              : <Chip tone="success">All paid</Chip>}
            <div className="text-[11px] text-ink-muted tabular-nums">{inr(c.collected)} of {inr(c.total)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <div className="flex-1"><Progress value={c.collected} total={c.total} /></div>
          <span className="text-[11px] font-semibold text-ink-muted">
            {c.players.length - unpaid.length}/{c.players.length} paid
          </span>
          <ChevronDown size={16} className={cn("text-ink-muted transition-transform", open && "rotate-180")} />
        </div>
      </button>

      {open && (
        <div className="border-t border-black/[0.06]">
          <div className="divide-y divide-black/[0.06]">
            {c.players.map((p) => <PlayerRow key={p.txn_id} p={p} markingPaid={markingPaid} onMark={onMark} />)}
          </div>
          {unpaid.length > 0 && (
            <div className="p-3 border-t border-black/[0.06]">
              <button type="button" onClick={reminder} data-testid={`remind-${c.id}`}
                className="w-full h-10 rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-semibold flex items-center justify-center gap-2 btn-press">
                <Share2 size={15} /> Remind {unpaid.length} on WhatsApp
              </button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function PlayerRow({ p, markingPaid, onMark }) {
  const s = STATUS[p.status];
  return (
    <div className="flex items-center gap-2 px-4 py-2.5">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">
          {p.name}{p.guest && <span className="text-[10px] uppercase text-amber-700 font-bold ml-1">guest</span>}
        </div>
        <div className="text-[11px] text-ink-muted">
          {inr(p.amount)}
          {p.status === "partial" && ` • ${inr(p.outstanding)} left`}
          {p.status === "paid" && p.marked_by && ` • marked by ${p.marked_by}`}
        </div>
      </div>
      <Chip tone={s.tone} className="text-[11px] shrink-0">{s.label}</Chip>
      {p.outstanding > 0 && (
        <Button size="sm" variant="outline" className="h-8 px-2 text-xs shrink-0"
          onClick={() => onMark(p)} disabled={markingPaid === p.txn_id} data-testid={`mark-paid-${p.txn_id}`}>
          <Check size={14} /> {markingPaid === p.txn_id ? "…" : "Paid"}
        </Button>
      )}
    </div>
  );
}

/** Everyone who's been charged: who owes what, biggest first. */
function MemberList({ rows, club, markingPaid, onMark }) {
  const [onlyPending, setOnlyPending] = useState(true);
  const [openId, setOpenId] = useState(null);
  const shown = onlyPending ? rows.filter((r) => r.pending > 0) : rows;
  const settled = rows.length - rows.filter((r) => r.pending > 0).length;

  const remindAll = () => {
    const due = rows.filter((r) => r.pending > 0);
    const lines = [
      `💸 *${club?.name || "Club"} — pending payments*`,
      "",
      ...due.map((r, i) => `${i + 1}. ${r.name}${r.guest ? " (Guest)" : ""} — ${inr(r.pending)}`),
    ];
    if (club?.upi_id) lines.push("", `Pay by UPI: ${club.upi_id}`);
    lines.push("", "Please pay and mark it as paid in Cricso 🙏", "", "Thanks,", "Cricso", "Powered by HMT Technologies");
    window.open(whatsappShareUrl(lines.join("\n")), "_blank");
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="grid grid-cols-2 gap-1 p-1 bg-black/[0.04] rounded-xl text-xs font-semibold">
          <button className={cn("h-8 px-3 rounded-lg", onlyPending ? "bg-white shadow-sm" : "text-ink-muted")} onClick={() => setOnlyPending(true)}>
            Pending
          </button>
          <button className={cn("h-8 px-3 rounded-lg", !onlyPending ? "bg-white shadow-sm" : "text-ink-muted")} onClick={() => setOnlyPending(false)}>
            Everyone
          </button>
        </div>
        {rows.some((r) => r.pending > 0) && (
          <Button size="sm" variant="outline" className="h-9 text-xs" onClick={remindAll} data-testid="remind-all">
            <Share2 size={14} /> Remind all
          </Button>
        )}
      </div>

      {shown.length === 0 ? (
        <Card className="text-sm text-ink-muted text-center">Nobody owes anything right now 🎉</Card>
      ) : (
        <Card className="p-0 divide-y divide-black/[0.06] overflow-hidden">
          {shown.map((r) => {
            const open = openId === r.member_id;
            return (
              <div key={r.member_id} data-testid={`member-due-${r.member_id}`}>
                <button type="button" className="w-full text-left flex items-center gap-3 px-4 py-3"
                  onClick={() => setOpenId(open ? null : r.member_id)} disabled={r.pending <= 0}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {r.name}{r.guest && <span className="text-[10px] uppercase text-amber-700 font-bold ml-1">guest</span>}
                    </div>
                    <div className="text-[11px] text-ink-muted tabular-nums">
                      Charged {inr(r.charged)} • Paid {inr(r.paid)}
                    </div>
                  </div>
                  {r.pending > 0 ? (
                    <>
                      <div className="text-sm font-bold text-red-500 tabular-nums">{inr(r.pending)}</div>
                      <ChevronDown size={16} className={cn("text-ink-muted transition-transform", open && "rotate-180")} />
                    </>
                  ) : <Chip tone="success" className="text-[11px]">Settled</Chip>}
                </button>
                {open && (
                  <div className="bg-black/[0.02] px-4 pb-3 space-y-1.5">
                    {r.pending_items.map((i) => (
                      <div key={i.txn_id} className="flex items-center gap-2 text-sm">
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{i.title}</div>
                          <div className="text-[11px] text-ink-muted">{fmtDate(i.date)}</div>
                        </div>
                        <span className="font-semibold tabular-nums text-red-500">{inr(i.outstanding)}</span>
                        <Button size="sm" variant="outline" className="h-8 px-2 text-xs"
                          onClick={() => onMark([i], r.name)} disabled={!!markingPaid}>
                          <Check size={14} /> Paid
                        </Button>
                      </div>
                    ))}
                    {r.pending_items.length > 1 && (
                      <Button size="sm" className="w-full h-9 mt-1" onClick={() => onMark(r.pending_items, r.name)}
                        disabled={!!markingPaid} data-testid={`mark-all-${r.member_id}`}>
                        <Check size={14} /> Mark all {inr(r.pending)} as paid
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      )}
      {onlyPending && settled > 0 && (
        <p className="text-xs text-ink-muted text-center">{settled} member{settled === 1 ? " has" : "s have"} paid everything.</p>
      )}
    </div>
  );
}

/** The full transaction log, with filters and CSV export (what the Ledger used to be). */
function AllEntries({ members }) {
  const [txns, setTxns] = useState(null);
  const [filter, setFilter] = useState({ member_id: "", category: "" });
  const memberName = (id) => members.find((m) => m.id === id)?.name || "—";

  useEffect(() => {
    const params = new URLSearchParams();
    if (filter.member_id) params.set("member_id", filter.member_id);
    if (filter.category) params.set("category", filter.category);
    setTxns(null);
    api.get(`/transactions?${params.toString()}`)
      .then(({ data }) => setTxns(data))
      .catch((err) => { toast.error(apiError(err)); setTxns([]); });
  }, [filter]);

  const exportCsv = () => {
    if (!txns || txns.length === 0) { toast.info("No transactions to export"); return; }
    const header = ["Date", "Member", "Description", "Category", "Amount"];
    const rows = txns.map((t) => [
      fmtDate(t.created_at), memberName(t.member_id), (t.description || "").replaceAll(",", " "), t.category || "", t.amount,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `ledger-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported CSV");
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Select value={filter.member_id} onChange={(e) => setFilter({ ...filter, member_id: e.target.value })} className="h-11 text-sm" data-testid="ledger-member-filter">
          <option value="">All members</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </Select>
        <Select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} className="h-11 text-sm capitalize" data-testid="ledger-category-filter">
          {CATS.map((c) => <option key={c || "all"} value={c}>{c || "All categories"}</option>)}
        </Select>
      </div>
      <div className="flex items-center justify-between text-xs text-ink-muted">
        <span>Every entry is kept — corrections are added as reversals.</span>
        <button onClick={exportCsv} className="h-8 px-3 rounded-full bg-black/5 font-semibold text-ink shrink-0" data-testid="ledger-export-btn">
          Export CSV
        </button>
      </div>
      {txns === null ? (
        <Skeleton className="h-64" />
      ) : txns.length === 0 ? (
        <Card className="p-0"><Empty icon={Wallet} title="No transactions" subtitle="Add a charge or record a payment to begin." /></Card>
      ) : (
        <Card className="p-2 divide-y divide-black/[0.06]">
          {txns.map((t) => {
            const positive = t.amount >= 0;
            const Icon = positive ? ArrowUpRight : ArrowDownRight;
            return (
              <div key={t.id} className="flex items-center gap-3 px-2 py-3" data-testid={`ledger-txn-${t.id}`}>
                <div className={`h-10 w-10 rounded-2xl grid place-items-center shrink-0 ${positive ? "bg-brand-50 text-brand" : "bg-red-50 text-red-500"}`}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{t.description || t.category}</div>
                  <div className="text-xs text-ink-muted truncate">{memberName(t.member_id)} • {fmtDate(t.created_at)}</div>
                </div>
                <div className={`text-sm font-bold tabular-nums ${positive ? "text-brand" : "text-red-500"}`}>{inr(t.amount)}</div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}

/** Money received outside the app (cash / UPI) — goes into the member's wallet and clears their oldest dues. */
function TopupSheet({ open, onClose, members, onSaved }) {
  const [form, setForm] = useState({ member_id: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payments/topup", { member_id: form.member_id, amount: Number(form.amount), note: form.note });
      toast.success("Payment recorded");
      onClose();
      setForm({ member_id: "", amount: "", note: "" });
      await onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title="Record payment">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-ink-muted -mt-1">
          Money a member gave you (cash / UPI). It clears their oldest pending charges first; anything extra stays as advance in their wallet.
        </p>
        <Select label="Member *" value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} required data-testid="topup-member">
          <option value="">Select member</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </Select>
        <Input label="Amount ₹ *" type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="topup-amount" />
        <Input label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Cash / UPI paid to captain" />
        <Button type="submit" size="lg" className="w-full" disabled={saving} data-testid="topup-submit">
          {saving ? "Saving…" : "Record payment"}
        </Button>
      </form>
    </Sheet>
  );
}
